import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, Truck, ClipboardList, FileText, Wallet,
  Download, ShieldCheck, ShieldAlert, Settings, LogOut, FileBarChart, Building2,
  Package, CalendarOff, KeyRound, UserCheck, Clock, Sparkles, Activity, Trash2, FileDown,
} from "lucide-react";
import { Breadcrumbs, GlobalSearch } from "./Shared";
import QuickActionMenu from "./QuickActionMenu";
import NotificationBell from "./NotificationBell";
import ExportJobsBell from "./ExportJobsBell";
import NotificationsBell from "./NotificationsBell";
import ThemeToggle from "./ThemeToggle";
import PreviewBanner from "./PreviewBanner";
import AuthHealthBanner from "./AuthHealthBanner";
import AdminMassDeleteHub from "./AdminMassDeleteHub";
import BackupHealthIndicator from "./BackupHealthIndicator";
import FirmSwitcher from "./../contexts/FirmContext";
import { usePermissions } from "@/contexts/PermissionsContext";
import { api, BACKEND_URL } from "@/lib/api";
import { FOCUS_MODE } from "@/lib/featureFlags";

// Phase 1 — Hardening — Sidebar respects `tenant.enabled_modules` from the API.
// Modules NOT in that list are hidden from the sidebar (and the backend will
// also block access). RBAC `can()` continues to layer on top.
function useTenantEnabledModules(user) {
  const [enabled, setEnabled] = useState(null); // null = still loading → show all
  useEffect(() => {
    if (!user) return;
    api.get("/tenants/me").then(({ data }) => {
      const list = Array.isArray(data?.enabled_modules) ? data.enabled_modules : null;
      setEnabled(list);
    }).catch(() => setEnabled(null));
  }, [user]);
  return enabled;
}

// DEMO_MODE: Hides SaaS/Super-Admin links from the sidebar for client demos.
// Toggle to `true` to hide Mandanten, Abo-Pakete.
// Phase 4 — disabled by default since SaaS layer is now production architecture.
const DEMO_MODE = (process.env.REACT_APP_DEMO_MODE || "false").toLowerCase() === "true";

const NAV_GROUPS = [
  {
    label: "Übersicht",
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard", module: "dashboard" },
      { to: "/operations/heute", label: "Operations", icon: ClipboardList, testid: "nav-operations", module: "operations" },
      { to: "/operations/aufgaben", label: "Aufgaben", icon: ClipboardList, testid: "nav-aufgaben", module: "operations", badgeKey: "tasks_open" },
      { to: "/zeit-anfragen", label: "Zeit-Anfragen", icon: Clock, testid: "nav-zeit-anfragen", module: "operations" },
    ],
  },
  {
    label: "Stammdaten",
    items: [
      { to: "/mitarbeiter", label: "Mitarbeiter", icon: Users, testid: "nav-mitarbeiter", module: "mitarbeiter" },
      { to: "/profilanfragen", label: "Profilanfragen", icon: UserCheck, testid: "nav-profilanfragen", module: "profilanfragen" },
      { to: "/fuhrpark", label: "Fuhrpark", icon: Truck, testid: "nav-fuhrpark", module: "fuhrpark" },
      { to: "/fuhrpark/uebergaben", label: "KFZ Übergaben", icon: ClipboardList, testid: "nav-uebergaben", module: "fuhrpark" },
      { to: "/karten", label: "Karten", icon: KeyRound, testid: "nav-karten", module: "karten" },
      { to: "/dokumente", label: "Dokumente", icon: FileText, testid: "nav-dokumente", module: "dokumente" },
      // Phase G F3 — vorher `strictAdminOrGfOnly` (kein Override). Jetzt
      // `adminOrGfOnly` damit Rollen mit expliziter `finanzen.view=true`
      // (z.B. Buchhalter) den Eintrag sehen. Backend-RBAC blockt unbefugte
      // API-Calls weiterhin via `require_permission("finanzen", "view")`.
      { to: "/finanzen", label: "Finanzen", icon: Wallet, testid: "nav-finanzen", module: "finanzen", adminOrGfOnly: true },
    ],
  },
  {
    label: "Werkzeuge",
    items: [
      // Phase 5Z.30 — Werkzeuge nur Admin/GF, ABER Permission-Override pro Eintrag möglich.
      { to: "/hr", label: "Abwesenheiten", icon: CalendarOff, testid: "nav-hr-absence", module: "abwesenheiten", adminOrGfOnly: true },
      { to: "/export", label: "Export", icon: Download, testid: "nav-export", module: "exports", adminOrGfOnly: true },
      { to: "/exports/downloads", label: "Export-Downloads", icon: FileDown, testid: "nav-exports-downloads", module: "exports", adminOrGfOnly: true },
      { to: "/reports", label: "Berichte", icon: FileBarChart, testid: "nav-reports", module: "reports", adminOrGfOnly: true },
      { to: "/audit", label: "Audit", icon: ShieldCheck, testid: "nav-audit", module: "audit", adminOrGfOnly: true },
      { to: "/security", label: "Sicherheit", icon: ShieldAlert, testid: "nav-security", roles: ["admin", "geschaeftsfuehrung"] },
      { to: "/firmenprofil", label: "Firmenprofil", icon: Building2, testid: "nav-firmenprofil", module: "firmenprofil", adminOrGfOnly: true },
      { to: "/erweiterungen", label: "Erweiterungen", icon: Sparkles, testid: "nav-erweiterungen", adminOnly: true },
      { to: "/einstellungen", label: "Einstellungen", icon: Settings, testid: "nav-einstellungen", module: "einstellungen", adminOrGfOnly: true },
      { to: "/einstellungen/lohn-zeit", label: "Lohn & Zeit", icon: Settings, testid: "nav-payroll-settings", module: "einstellungen", adminOrGfOnly: true },
    ],
  },
  {
    label: "Platform",
    superAdminOnly: true,
    items: [
      { to: "/super-admin/cockpit", label: "Owner Cockpit", icon: Activity, testid: "nav-sa-cockpit" },
      { to: "/super-admin/tenants", label: "Mandanten", icon: Building2, testid: "nav-tenants" },
      { to: "/super-admin/packages", label: "Abo-Pakete", icon: Package, testid: "nav-packages" },
      { to: "/super-admin/extensions", label: "Erweiterungen-Katalog", icon: Sparkles, testid: "nav-sa-extensions" },
      { to: "/super-admin/health", label: "System & Backup", icon: ShieldCheck, testid: "nav-sa-health" },
    ],
  },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { can, isPreviewing, role: effectiveRole, ready: permsReady } = usePermissions();
  const location = useLocation();
  const [logoBust, setLogoBust] = useState(Date.now());
  const [hasLogo, setHasLogo] = useState(false);
  const [companyName, setCompanyName] = useState("Munja Trans GmbH");
  const [tenantId, setTenantId] = useState("");
  const [badges, setBadges] = useState({ tasks_open: 0 });
  const [adminWipeOpen, setAdminWipeOpen] = useState(false);
  const tenantEnabledModules = useTenantEnabledModules(user);
  const isStaging = (process.env.REACT_APP_ENV || "production").toLowerCase() === "staging";

  useEffect(() => {
    if (!user) return;
    let mounted = true;
    async function loadBadges() {
      try {
        const { data } = await api.get("/tasks/_summary");
        if (mounted) setBadges({ tasks_open: data?.open || 0 });
      } catch { /* ignore */ }
    }
    loadBadges();
    const id = setInterval(loadBadges, 60_000);
    return () => { mounted = false; clearInterval(id); };
  }, [user, location.pathname]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    function loadProfile() {
      // Prefer new tenant-scoped profile (Phase 4G); fallback to legacy settings
      api.get("/profile/company").then(({ data }) => {
        if (cancelled) return;
        setHasLogo(!!data.logo_path);
        if (data.name) setCompanyName(data.name);
        if (data.tenant_id) setTenantId(data.tenant_id);
      }).catch(() => {
        api.get("/settings/company").then(({ data }) => {
          if (cancelled) return;
          setHasLogo(!!data.logo_path);
          if (data.name) setCompanyName(data.name);
        }).catch(() => {});
      });
    }
    loadProfile();
    function refresh() { setLogoBust(Date.now()); loadProfile(); }
    window.addEventListener("company-logo-changed", refresh);
    window.addEventListener("company-profile-changed", refresh);
    return () => {
      cancelled = true;
      window.removeEventListener("company-logo-changed", refresh);
      window.removeEventListener("company-profile-changed", refresh);
    };
  }, [user]);

  if (user === null) return <div className="min-h-screen flex items-center justify-center text-zinc-400">Lade...</div>;
  if (user === false) return <Navigate to="/login" state={{ from: location }} replace />;
  // External accountants must use their dedicated portal — never the main app shell.
  if (user.external_accountant_id) return <Navigate to="/steuerberater-portal" replace />;

  // Prefer new public tenant logo URL (Phase 4G), fallback to legacy, else Munja default logo
  const logoUrl = hasLogo
    ? (tenantId
        ? `${BACKEND_URL}/api/public/tenant/${tenantId}/logo?ts=${logoBust}`
        : `${BACKEND_URL}/api/settings/company/logo?ts=${logoBust}`)
    : "/munja-logo.png";

  return (
    <div className="min-h-screen flex bg-zinc-950 text-zinc-100">
      {/* Sidebar */}
      <aside className="w-56 bg-zinc-950 border-r border-zinc-800 flex flex-col shrink-0" data-testid="app-sidebar">
        <div className="min-h-[64px] py-2 px-4 flex items-center border-b border-zinc-800 gap-3" data-testid="sidebar-brand">
          <img src={logoUrl} alt="Munja Trans GmbH" className="h-10 w-10 rounded object-contain bg-zinc-900 p-0.5 shrink-0" data-testid="sidebar-logo-img"/>
          <div className="min-w-0">
            <div className="font-display font-bold text-zinc-50 leading-tight text-sm truncate">{companyName || "Munja Trans GmbH"}</div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-amber-500/80 mt-0.5">seit 2011</div>
          </div>
        </div>
        <nav className="flex-1 py-3 overflow-y-auto">
          {/* Phase 5Z.19n — Render skeleton until BOTH permissions matrix &&
              tenant.enabled_modules are loaded. Prevents the
              "flash of unauthorized content" where a dispatcher briefly saw
              admin items during their first paint after refresh. */}
          {(!permsReady || tenantEnabledModules === null) ? (
            <div className="px-5 space-y-1.5" data-testid="sidebar-loading">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-7 rounded bg-zinc-900/60 animate-pulse" />
              ))}
            </div>
          ) : NAV_GROUPS.map((g) => {
            // While previewing as another role, hide super-admin section
            const realSuperAdmin = !!user?.is_super_admin && !isPreviewing;
            const realAdmin = (user?.role === "admin" && !isPreviewing) || realSuperAdmin;
            if (g.superAdminOnly && !realSuperAdmin) return null;
            // FOCUS_MODE: hide the entire Platform / SUPER group while the
            // app is in single-tenant mode (not yet sold as SaaS).
            if (FOCUS_MODE && g.superAdminOnly) return null;
            // DEMO_MODE: hide entire Super-Admin / Platform group EXCEPT health
            if (DEMO_MODE && g.superAdminOnly) {
              // Filter to only health-monitoring items in demo mode
              g = { ...g, items: g.items.filter((it) => it.testid === "nav-sa-health") };
              if (g.items.length === 0) return null;
            }
            const visibleItems = g.items.filter((n) => {
              if (n.adminOnly && !realAdmin && effectiveRole !== "admin") return false;
              // Phase 5Z.30 — strictAdminOrGfOnly: hart auf admin/GF beschränkt,
              // KEIN Permission-Override (z.B. Finanzen).
              if (n.strictAdminOrGfOnly) {
                const isPriv = effectiveRole === "admin" || effectiveRole === "geschaeftsfuehrung";
                if (!isPriv) return false;
              }
              // Phase 5Z.30 — adminOrGfOnly: admin/GF, ODER eine andere Rolle
              // mit expliziter `view`-Permission für das Modul (Werkzeuge-Gruppe).
              if (n.adminOrGfOnly) {
                const isPriv = effectiveRole === "admin" || effectiveRole === "geschaeftsfuehrung";
                if (!isPriv) {
                  if (!n.module || !can(n.module, "view")) return false;
                }
              }
              // FOCUS_MODE: hide SaaS-related tenant items (self-billing & addon catalog)
              // FOCUS_MODE: hide SaaS-related tenant items (addon catalog)
              if (FOCUS_MODE && n.testid === "nav-erweiterungen") return false;
              if (n.roles && !n.roles.includes(effectiveRole) && !realSuperAdmin) return false;
              // Phase 1 — Tenant module gate: if tenant.enabled_modules is set and
              // the module is not in the allowed list, hide it.
              if (n.module && tenantEnabledModules && !tenantEnabledModules.includes(n.module)) {
                return false;
              }
              return !n.module || can(n.module, "view");
            });
            if (visibleItems.length === 0) return null;
            return (
              <div key={g.label} className="mb-4">
                <div className="px-5 py-1 text-[9px] uppercase tracking-[0.2em] text-zinc-600 font-semibold">
                  {g.label}
                  {g.superAdminOnly && <span className="ml-1.5 text-[8px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30">SUPER</span>}
                </div>
                {visibleItems.map((n) => {
                  const badgeCount = n.badgeKey ? badges[n.badgeKey] : 0;
                  return (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    data-testid={n.testid}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-5 py-2 text-sm border-l-2 transition-colors ${
                        isActive
                          ? "bg-zinc-900 text-zinc-50 font-medium border-amber-500"
                          : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/50 border-transparent"
                      }`
                    }
                  >
                    <n.icon className="w-4 h-4" />
                    <span className="flex-1">{n.label}</span>
                    {badgeCount > 0 && (
                      <span data-testid={`${n.testid}-badge`}
                            className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold inline-flex items-center justify-center">
                        {badgeCount > 99 ? "99+" : badgeCount}
                      </span>
                    )}
                  </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>
        <div className="border-t border-zinc-800 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-medium text-zinc-100 truncate">{user.name || user.email}</div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider">{user.role}</div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="p-2 text-zinc-400 hover:text-red-400 hover:bg-zinc-900 rounded-md transition-colors"
              title="Abmelden"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-3 pt-3 border-t border-zinc-800/60 text-center">
            <div className="text-[9px] uppercase tracking-[0.18em] text-zinc-600 italic" data-testid="powered-by">
              Entwicklung bei <span className="text-amber-500/70 not-italic font-semibold">Ichkov</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <PreviewBanner />
        <AuthHealthBanner user={user} />
        {isStaging && (
          <div data-testid="staging-banner"
               className="h-9 bg-amber-500/15 border-b border-amber-500/40 flex items-center justify-center gap-2 text-amber-200 text-xs font-semibold">
            <span className="px-2 py-0.5 rounded bg-amber-500/30 text-amber-100 text-[10px] font-bold tracking-wider uppercase">Staging</span>
            <span>Test-Umgebung — Änderungen wirken sich NICHT auf Munja Trans Production aus</span>
          </div>
        )}
        <header className="h-16 bg-zinc-950 border-b border-zinc-800 flex items-center justify-between px-6 shrink-0 gap-4">
          {/* Left: Breadcrumbs */}
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <Breadcrumbs />
          </div>

          {/* Center: Global search */}
          <div className="shrink-0">
            <GlobalSearch />
          </div>

          {/* Right: Quick actions + status */}
          <div className="flex items-center gap-2 shrink-0">
            <FirmSwitcher />
            <QuickActionMenu />
            <ExportJobsBell />
            <NotificationBell />
            {user?.is_super_admin && <NotificationsBell />}
            {(user?.role === "admin" || user?.is_super_admin) && (
              <button
                onClick={() => setAdminWipeOpen(true)}
                data-testid="admin-mass-delete-open"
                title="Massen-Löschung (Admin)"
                className="p-1.5 text-zinc-500 hover:text-rose-400 hover:bg-rose-500/10 rounded transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <ThemeToggle />
            {(user?.role === "admin" || user?.role === "geschaeftsfuehrung") && (
              <BackupHealthIndicator />
            )}
            <div className="w-px h-6 bg-zinc-800 mx-1" />
            <div className="flex items-center gap-2 text-xs">
              <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
              <span className="text-zinc-400">System OK</span>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto bg-zinc-950">
          <Outlet />
        </main>
        <AdminMassDeleteHub
          user={user}
          open={adminWipeOpen}
          onClose={() => setAdminWipeOpen(false)}
        />
      </div>
    </div>
  );
}
